const SCRIPT_SELECTOR = 'script[data-google-maps-loader="true"]';
const MAX_LOAD_ATTEMPTS = 3;
const BASE_BACKOFF_MS = 500;
const ATTEMPT_TIMEOUT_MS = 8000;

const TRANSIENT_PLACES_STATUSES = ["UNKNOWN_ERROR", "OVER_QUERY_LIMIT"];
const CALLBACK_NAMESPACE = "__tracyMapsCallbacks";

let callbackSeq = 0;

let loadPromise: Promise<void> | null = null;
let authFailed = false;
let rejectInFlight: ((error: Error) => void) | null = null;

const mapsWindow = () => window as any;

function mapsApiKey(): string | undefined {
  return process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
}

function mapsReady(): boolean {
  const maps = mapsWindow().google?.maps;
  return Boolean(maps?.importLibrary || maps?.places);
}

function removeLoaderScripts(): void {
  document.querySelectorAll(SCRIPT_SELECTOR).forEach((el) => el.remove());
}

/** Maps calls this when the key is rejected (bad key, disabled billing, referer block). */
function installAuthFailureHook(): void {
  if (mapsWindow().gm_authFailure) return;
  mapsWindow().gm_authFailure = () => {
    authFailed = true;
    loadPromise = null;
    removeLoaderScripts();
    rejectInFlight?.(new Error("Google Maps rejected the API key"));
  };
}

const wait = (ms: number) =>
  new Promise<void>((resolve) => setTimeout(resolve, ms));

/** A blocked Maps request never fires load or error, so bound every attempt. */
function withTimeout<T>(
  promise: Promise<T>,
  ms: number,
  message: string,
): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const timer = setTimeout(() => reject(new Error(message)), ms);
    promise.then(
      (value) => {
        clearTimeout(timer);
        resolve(value);
      },
      (error) => {
        clearTimeout(timer);
        reject(error);
      },
    );
  });
}

function injectScript(key: string): Promise<void> {
  return new Promise((resolve, reject) => {
    removeLoaderScripts();
    rejectInFlight = reject;

    const settle = (run: () => void) => {
      rejectInFlight = null;
      run();
    };

    // loading=async suppresses the script's load event as a readiness signal,
    // so Maps' own callback is the only reliable "API is usable now" hook.
    const w = mapsWindow();
    const callbacks = (w[CALLBACK_NAMESPACE] ??= {});
    const callbackKey = `cb${++callbackSeq}`;
    const callbackPath = `${CALLBACK_NAMESPACE}.${callbackKey}`;

    const done = (run: () => void) => {
      delete callbacks[callbackKey];
      settle(run);
    };

    callbacks[callbackKey] = () => {
      if (!mapsReady()) {
        script.remove();
        done(() =>
          reject(new Error("Google Maps loaded without a places library")),
        );
        return;
      }
      done(resolve);
    };

    const script = document.createElement("script");
    script.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(key)}&libraries=places,maps&loading=async&callback=${callbackPath}`;
    script.async = true;
    script.defer = true;
    script.dataset.googleMapsLoader = "true";

    script.addEventListener("error", () => {
      // Dropping the tag is what lets the next attempt inject a live one.
      script.remove();
      done(() => reject(new Error("Failed to load the Google Maps script")));
    });

    document.head.appendChild(script);
  });
}

async function loadWithRetry(key: string): Promise<void> {
  let lastError: unknown = new Error("Failed to load the Google Maps script");

  for (let attempt = 1; attempt <= MAX_LOAD_ATTEMPTS; attempt++) {
    if (authFailed) throw new Error("Google Maps rejected the API key");
    try {
      await withTimeout(
        injectScript(key),
        ATTEMPT_TIMEOUT_MS,
        "Timed out loading the Google Maps script",
      );
      return;
    } catch (error) {
      lastError = error;
      // Retrying a rejected key cannot succeed, so stop and surface it.
      if (authFailed) break;
      if (attempt < MAX_LOAD_ATTEMPTS) {
        await wait(BASE_BACKOFF_MS * 2 ** (attempt - 1));
      }
    }
  }

  throw lastError;
}

export function loadGoogleMaps(): Promise<void> {
  if (mapsReady()) return Promise.resolve();
  if (authFailed) {
    return Promise.reject(new Error("Google Maps rejected the API key"));
  }
  if (loadPromise) return loadPromise;

  const key = mapsApiKey();
  if (!key) {
    return Promise.reject(
      new Error("Missing REACT_APP_GOOGLE_MAPS_API_KEY"),
    );
  }

  installAuthFailureHook();
  loadPromise = loadWithRetry(key).catch((error) => {
    // Clear the memo so a later caller starts a fresh attempt rather than
    // awaiting a promise that can never settle again.
    loadPromise = null;
    throw error;
  });

  return loadPromise;
}

export async function loadPlacesLibrary(): Promise<any> {
  await loadGoogleMaps();
  const maps = mapsWindow().google?.maps;
  if (maps?.importLibrary) {
    return withTimeout(
      Promise.resolve(maps.importLibrary("places")),
      ATTEMPT_TIMEOUT_MS,
      "Timed out loading the Google Maps places library",
    );
  }
  return maps?.places;
}

/** Drops cached state so the next load re-injects the script from scratch. */
export function resetGoogleMapsLoader(): void {
  loadPromise = null;
  authFailed = false;
  removeLoaderScripts();
}

export function isTransientPlacesStatus(status: string): boolean {
  return TRANSIENT_PLACES_STATUSES.includes(status);
}
